import { useMemo } from "react";

import supportedDashboardBrowsers from "../../../../public/supported-dashboard-browsers.json";
import supportedFormsBrowsers from "../../../../public/supported-forms-browsers.json";

import { isTest } from "@/common/constants/env";
import { parseUserAgent } from "@/common/helpers/parseUserAgent";
import { SupportedBrowser } from "@/features/UnsupportedBrowser/all-supported-browsers";

function isInAppBrowser(browserName?: string): boolean {
  if (!browserName) {
    return false;
  }

  return [
    "Facebook",
    "Instagram",
    "WhatsApp",
    "Twitter",
    "LinkedIn",
    "Messenger",
    "TikTok",
    "WeChat",
    "Pinterest",
  ].includes(browserName);
}

// Modern, actively-maintained browsers that self-identify by name rather than spoofing Chrome/Safari.
// All are Chromium- or WebKit-based and auto-update — always let them through.
const MODERN_PASSTHROUGH_BROWSERS = new Set([
  "Brave", // Chromium-based; iOS Brave v1.78+ reports "Brave/1" in the UA string via ua-parser-js
  "Vivaldi", // Chromium-based desktop browser
  "DuckDuckGo", // WebKit-based privacy browser (iOS)
  "AVG Secure Browser", // Chromium-based security browser
  "Avast Secure Browser", // Chromium-based security browser, same vendor as AVG
]);

type GetCurrentBrowserParams = {
  browserName?: string;
  browserVersion?: string;
  isAndroid?: boolean;
  userAgent?: string;
};

export function getCurrentBrowser({
  browserName,
  browserVersion,
  isAndroid,
}: GetCurrentBrowserParams): { browserKey: SupportedBrowser | "in-app"; browserVersion?: string } | null {
  if (isInAppBrowser(browserName)) {
    return { browserKey: "in-app", browserVersion: undefined };
  }

  // ua-parser-js v2 names mobile and embedded variants separately ("Mobile Chrome", "Chrome WebView", …);
  // collapse them back to the desktop name, since they report the version of the engine they embed.
  if (
    browserName === "Chrome" ||
    browserName === "Mobile Chrome" ||
    browserName === "Chrome WebView" ||
    browserName === "Brave" ||
    browserName === "AVG Secure Browser" ||
    browserName === "Avast Secure Browser"
  ) {
    return { browserKey: isAndroid ? "and_chr" : "chrome", browserVersion };
  }

  if (
    browserName === "Edge" ||
    browserName === "Mobile Edge" ||
    browserName === "Edge WebView" ||
    browserName === "Edge WebView2"
  ) {
    return { browserKey: "edge", browserVersion };
  }

  if (browserName === "Firefox" || browserName === "Mobile Firefox") {
    return { browserKey: isAndroid ? "and_ff" : "firefox", browserVersion };
  }

  if (browserName === "Safari") {
    return { browserKey: "safari", browserVersion };
  }

  // ua-parser-js v2 renamed "Samsung Browser" to "Samsung Internet".
  if (browserName === "Samsung Browser" || browserName === "Samsung Internet") {
    return { browserKey: "samsung", browserVersion };
  }

  // The dashboard criteria exclude Android WebView ("not Android > 0"), so this key only exists for forms.
  if (browserName === "Android Browser") {
    return { browserKey: "android", browserVersion };
  }

  if (browserName === "Mobile Safari") {
    return { browserKey: "ios_saf", browserVersion };
  }

  // Opera for Android reports plain "Opera" with mobile version numbering (~76), below the desktop floor.
  if (browserName === "Opera") {
    return { browserKey: isAndroid ? "op_mob" : "opera", browserVersion };
  }

  if (browserName === "Opera Mobi" || browserName === "Opera Tablet" || browserName === "Opera Mobile") {
    return { browserKey: "op_mob", browserVersion };
  }

  return null;
}

type IsBrowserSupportedParams = {
  browserName?: string;
  browserVersion?: string;
  supportedBrowsers: Record<string, number[]>;
  isAndroid?: boolean;
};

export const isBrowserSupported = ({
  browserName,
  browserVersion,
  supportedBrowsers,
  isAndroid,
}: IsBrowserSupportedParams): boolean => {
  if (isTest || process.env.NEXT_PUBLIC_SKIP_BROWSER_CHECK === "true") {
    return true;
  }

  if (!browserName || !browserVersion) {
    return false;
  }

  if (browserName === "GSA" || isInAppBrowser(browserName) || MODERN_PASSTHROUGH_BROWSERS.has(browserName)) {
    return true;
  }

  const browser = getCurrentBrowser({ browserName, browserVersion, isAndroid });

  if (!browser) {
    return false;
  }

  const supportedVersions = supportedBrowsers[browser.browserKey];
  if (!supportedVersions || supportedVersions.length === 0) {
    return false;
  }

  // Extract major version number from browser version string
  const majorVersion = parseInt(browser.browserVersion?.split(".")[0] ?? "unknown", 10);
  if (isNaN(majorVersion)) {
    return false;
  }

  const minimalSupportedVersion = supportedVersions[0];

  return majorVersion >= minimalSupportedVersion;
};

export const useIsUnsupportedBrowserForDashboard = () => {
  return useMemo(() => {
    const { browser, os } = parseUserAgent();
    return !isBrowserSupported({
      browserName: browser.name,
      browserVersion: browser.version,
      supportedBrowsers: supportedDashboardBrowsers,
      isAndroid: os.name === "Android",
    });
  }, []);
};

export const useIsUnsupportedBrowserForForms = () => {
  return useMemo(() => {
    const { browser, os } = parseUserAgent();
    return !isBrowserSupported({
      browserName: browser.name,
      browserVersion: browser.version,
      supportedBrowsers: supportedFormsBrowsers,
      isAndroid: os.name === "Android",
    });
  }, []);
};
